import { Add, Check } from "@similarweb/icons";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { Button, EmptyState, ListContentLoader } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { IGoogleAdsAccountOption } from "../hooks/use-google-ads-accounts";
import { useGoogleAdsAdGroups, type IGoogleAdsAdGroupListItem } from "../hooks/use-google-ads-ad-groups";

// A removed ad group isn't a real candidate to pick from — Google Ads' own UI hides
// these by default too, so the picker excludes them rather than letting them be selected.
const REMOVED_AD_GROUP_STATUS = "REMOVED";

interface IGoogleAdsAdGroupsStepHeaderProps {
    account: IGoogleAdsAccountOption;
    campaignName: string;
}

interface IGoogleAdsAdGroupsStepFooterProps {
    onBack: () => void;
    onNext: () => void;
    isNextDisabled: boolean;
    isNextLoading?: boolean;
}

interface IGoogleAdsAdGroupsStepProps {
    account: IGoogleAdsAccountOption;
    campaignId: string;
    selectedAdGroup: IGoogleAdsAdGroupListItem | null;
    onSelectAdGroup: (adGroup: IGoogleAdsAdGroupListItem) => void;
}

interface IGoogleAdsAdGroupListItemProps {
    adGroup: IGoogleAdsAdGroupListItem;
    isSelected: boolean;
    onClick: () => void;
}

export const GoogleAdsAdGroupsStepHeader = ({ account, campaignName }: IGoogleAdsAdGroupsStepHeaderProps) => {
    return (
        <Stack gap="4px">
            <Text
                fontVariant="h5_2"
                color={colorsPalettes.carbon["500"]}
            >
                Ad groups
            </Text>
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon["300"]}
            >
                Account: {account.name} ({account.id}) · Campaign: {campaignName}
            </Text>
        </Stack>
    );
};

export const GoogleAdsAdGroupsStepFooter = ({
    onBack,
    onNext,
    isNextDisabled,
    isNextLoading,
}: IGoogleAdsAdGroupsStepFooterProps) => {
    return (
        <Flex
            width="100%"
            justifyContent="space-between"
        >
            <Button
                type="flat"
                onClick={onBack}
                isDisabled={isNextLoading}
                data-automation="campaign-wizard-google-ads-ad-group-back-button"
            >
                Back to campaigns
            </Button>
            <Button
                onClick={onNext}
                isLoading={isNextLoading}
                isDisabled={isNextDisabled || isNextLoading}
                data-automation="campaign-wizard-google-ads-ad-group-next-button"
            >
                Next
            </Button>
        </Flex>
    );
};

const GoogleAdsAdGroupListItemRow = ({ adGroup, isSelected, onClick }: IGoogleAdsAdGroupListItemProps) => {
    return (
        <li>
            <Flex
                as="button"
                type="button"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                onClick={onClick}
                padding="8px 12px"
                transition="box-shadow 0.2s ease-in-out"
                borderRadius="3px"
                cursor="pointer"
                textAlign="left"
                whenHover={{ boxShadow: elevation.Z1 }}
                background={isSelected ? colorsPalettes.blue["50"] : colorsPalettes.carbon["50"]}
                border={`1px solid ${isSelected ? colorsPalettes.blue["400"] : colorsPalettes.carbon["50"]}`}
                aria-pressed={isSelected}
                data-automation={`campaign-wizard-google-ads-ad-group-${adGroup.id}`}
            >
                <Stack gap="2px">
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon["500"]}
                    >
                        {adGroup.name}
                    </Text>
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon["300"]}
                    >
                        ID: {adGroup.id} · Status: {adGroup.status}
                    </Text>
                </Stack>
                <Flex
                    inline
                    alignItems="center"
                    gap="4px"
                    data-automation={`campaign-wizard-google-ads-ad-group-select-${adGroup.id}`}
                >
                    {isSelected ? (
                        <Check
                            size="xs"
                            color={colorsPalettes.blue["400"]}
                        />
                    ) : (
                        <Add
                            size="xs"
                            color={colorsPalettes.blue["400"]}
                        />
                    )}
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.blue["400"]}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </Text>
                </Flex>
            </Flex>
        </li>
    );
};

export const GoogleAdsAdGroupsStep = ({
    account,
    campaignId,
    selectedAdGroup,
    onSelectAdGroup,
}: IGoogleAdsAdGroupsStepProps) => {
    const { data: adGroups, isLoading, isError, error } = useGoogleAdsAdGroups(account, campaignId);
    const selectableAdGroups = (adGroups ?? []).filter((adGroup) => adGroup.status !== REMOVED_AD_GROUP_STATUS);

    return (
        <Stack
            gap="16px"
            data-automation="campaign-wizard-google-ads-ad-groups"
        >
            {isLoading ? (
                <ListContentLoader loaderTitle="Loading your Google Ads ad groups…" />
            ) : isError ? (
                <EmptyState
                    title="Couldn't load Google Ads ad groups"
                    subtitle={error instanceof Error ? error.message : "Unknown error"}
                />
            ) : selectableAdGroups.length > 0 ? (
                <Stack
                    as="ul"
                    gap="8px"
                    data-automation="campaign-wizard-google-ads-ad-group-list"
                >
                    {selectableAdGroups.map((adGroup) => (
                        <GoogleAdsAdGroupListItemRow
                            key={adGroup.id}
                            adGroup={adGroup}
                            isSelected={selectedAdGroup?.id === adGroup.id}
                            onClick={() => onSelectAdGroup(adGroup)}
                        />
                    ))}
                </Stack>
            ) : (
                <EmptyState
                    title="No active ad groups found"
                    subtitle="This campaign has no active ad groups to pick from."
                />
            )}
        </Stack>
    );
};
